import { type AxiosRequestConfig } from 'axios';

import { axiosInstance, downloadBlobDataFile } from '~/services';

type TypeUseAPI<TypeResponse extends { data: unknown } = { data: unknown }> = {
  endpoint: string;
  /**
   * @default 'get'
   */
  method?: 'get' | 'post' | 'put' | 'delete' | 'patch';
  axiosConfig?: AxiosRequestConfig;
  /**
   * @default 'json'
   */
  responseType?: 'json' | 'blob';
  mockResponse?: TypeResponse;
};

const useAPI = <TypeParams = unknown, TypeResponse extends { data: unknown } = { data: unknown }>({
  endpoint,
  method = 'get',
  axiosConfig = {},
  responseType = 'json',
  mockResponse,
}: TypeUseAPI<TypeResponse>) => {
  const methodConfig = {
    get: (pathParams: string, params: TypeParams, config: AxiosRequestConfig) =>
      axiosInstance.get<TypeResponse>(endpoint + pathParams, { params, ...config }),
    post: (pathParams: string, params: TypeParams, config: AxiosRequestConfig) =>
      axiosInstance.post<TypeResponse>(endpoint + pathParams, params, config),
    put: (pathParams: string, params: TypeParams, config: AxiosRequestConfig) =>
      axiosInstance.put<TypeResponse>(endpoint + pathParams, params, config),
    delete: (pathParams: string, params: TypeParams, config: AxiosRequestConfig) =>
      axiosInstance.delete<TypeResponse>(endpoint + pathParams, { params, ...config }),
    patch: (pathParams: string, params: TypeParams, config: AxiosRequestConfig) =>
      axiosInstance.patch<TypeResponse>(endpoint + pathParams, params, config),
  };

  const execute = async ({
    params = {} as TypeParams,
    pathParams = '',
  }: {
    params?: TypeParams;
    pathParams?: string;
  }) => {
    if (mockResponse) {
      console.warn(`[mock API 호출]
- 엔드 포인트: ${method.toUpperCase() + ' ' + endpoint + pathParams}
- 요청 파라미터: ${JSON.stringify(params, null, 2)}
- 반환 값: ${JSON.stringify(mockResponse, null, 2)}`);

      return mockResponse;
    }

    const apiCallFn = methodConfig[method];

    if (!apiCallFn) {
      throw new Error(`지원하지 않는 메서드입니다: ${method}`);
    }

    const response = await apiCallFn(pathParams, params, {
      ...axiosConfig,
      responseType,
    });

    if (responseType === 'blob') {
      // blob 응답이 실제로 에러 JSON인지 확인
      const contentType = response.headers['content-type'];

      if (contentType?.includes('application/json')) {
        // JSON 형식이면 에러 응답일 가능성이 있음
        const blob = response.data as unknown as Blob;
        const text = await blob.text();
        const jsonData = JSON.parse(text);

        if (jsonData.error) {
          // 에러 객체가 있으면 에러를 throw
          throw jsonData.error;
        }
      }

      downloadBlobDataFile(response);
    }

    return response.data;
  };

  return execute;
};

export default useAPI;
